import { ChartColors } from "atom/chart/ChartBaseConfig";
import type { ComponentProps } from "react";
import Chart from "molecule/chart/Chart";
import { LoincCodingCode } from "src/constants/fhir";

/** Blood glucose is flagged out-of-range below this value (mg/dL). */
export const GLUCOSE_LOW_THRESHOLD = 70;
/** Blood glucose is flagged out-of-range above this value (mg/dL). */
export const GLUCOSE_HIGH_THRESHOLD = 180;

/** Red when out of the healthy glucose range, green otherwise. */
export const getGlucoseDotColor = (value: number): string =>
  value < GLUCOSE_LOW_THRESHOLD || value > GLUCOSE_HIGH_THRESHOLD
    ? ChartColors.Red
    : ChartColors.Green;

type ChartConfig = Pick<
  ComponentProps<typeof Chart>,
  "type" | "lines" | "bars" | "yAxis" | "referenceLines"
>;

const glucoseDot = (props: {
  cx: number;
  cy: number;
  payload: { primaryValue: number };
}) => (
  <circle
    cx={props.cx}
    cy={props.cy}
    r={6}
    fill={getGlucoseDotColor(props.payload.primaryValue)}
    stroke="white"
    strokeWidth={2}
  />
);

const glucoseActiveDot = (props: unknown) => {
  const { cx, cy, payload } = props as {
    cx: number;
    cy: number;
    payload: { primaryValue: number };
  };
  return (
    <circle
      cx={cx}
      cy={cy}
      r={8}
      fill={getGlucoseDotColor(payload.primaryValue)}
      stroke="white"
      strokeWidth={3}
    />
  );
};

/** Per-LOINC-code chart presentation: yAxis domain/ticks, reference lines, and colors. */
export const ChartConfigByLoincCode: Partial<
  Record<LoincCodingCode, ChartConfig>
> = {
  [LoincCodingCode.waistCircumference]: {
    type: "line",
    lines: [{ color: ChartColors.Violet, dataKey: "primaryValue" }],
    yAxis: {
      domain: [20, 60],
      padding: { bottom: 10 }
    }
  },
  [LoincCodingCode.weight]: {
    type: "line",
    lines: [{ color: ChartColors.Pink, dataKey: "primaryValue" }],
    yAxis: {
      domain: [140, 180],
      padding: { bottom: 10 }
    }
  },
  [LoincCodingCode.hba1c]: {
    type: "line",
    lines: [{ color: ChartColors.Orange, dataKey: "primaryValue" }],
    yAxis: { ticks: [5.7, 6.5], domain: [4.8, 7] },
    referenceLines: [
      { y: 5.7, stroke: ChartColors.Green },
      { y: 6.5, stroke: ChartColors.Orange }
    ]
  },
  [LoincCodingCode.stepsInDay]: {
    type: "bar",
    bars: [{ color: ChartColors.VioletLight, dataKey: "primaryValue" }],
    yAxis: {
      ticks: [5000, 10000, 15000],
      domain: [0, 20000]
    }
  },
  [LoincCodingCode.bloodPressure]: {
    type: "line",
    lines: [
      { color: ChartColors.Blue, dataKey: "primaryValue" },
      { color: ChartColors.BlueLight, dataKey: "secondaryValue" }
    ],
    yAxis: {
      ticks: [50, 100, 150, 200],
      domain: [0, 250]
    }
  },
  [LoincCodingCode.muscleMassPercentage]: {
    type: "line",
    lines: [
      { color: ChartColors.Green, dataKey: "primaryValue" },
      { color: ChartColors.Violet, dataKey: "secondaryValue" }
    ],
    yAxis: { ticks: [0, 15, 30, 45, 60, 75, 90], domain: [0, 90] }
  },
  [LoincCodingCode.bloodGlucoseCapillary]: {
    type: "line",
    yAxis: {
      ticks: [GLUCOSE_LOW_THRESHOLD, GLUCOSE_HIGH_THRESHOLD],
      domain: [40, 250]
    },
    referenceLines: [
      { y: GLUCOSE_LOW_THRESHOLD, stroke: ChartColors.Green },
      { y: GLUCOSE_HIGH_THRESHOLD, stroke: ChartColors.Green }
    ],
    lines: [
      {
        color: ChartColors.Blue,
        dataKey: "primaryValue",
        props: {
          stroke: "none",
          dot: glucoseDot,
          activeDot: glucoseActiveDot
        }
      }
    ]
  }
};

export const buildChartConfig = (
  loincCode?: LoincCodingCode
): ChartConfig | undefined =>
  loincCode ? ChartConfigByLoincCode[loincCode] : undefined;

/** Per-LOINC-code yAxis tick formatters that depend on component-scoped helpers. */
export const getYAxisTickFormatter = (
  loincCode: LoincCodingCode | undefined,
  formatValueRemoveZeroDecimals: (value: number) => string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ((value: any, index: number) => string) | undefined => {
  switch (loincCode) {
    case LoincCodingCode.waistCircumference:
      return (value) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        typeof value === "number"
          ? formatValueRemoveZeroDecimals(value)
          : value;
    case LoincCodingCode.weight:
      return (value) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        typeof value === "number" ? String(Math.round(value)) : value;
    case LoincCodingCode.stepsInDay:
      return (value: number) =>
        `${(value / 1000).toFixed(1).replace(/\.0$/, "")}K`;
    default:
      return undefined;
  }
};
